import { useEffect, useRef, useState } from 'react'
import { Search, X, User as UserIcon, Check } from 'lucide-react'
import { usersService, User } from '../services/users'
import { useTranslation } from '../i18n/I18nContext'

interface UserPickerProps {
  /** Currently selected user id (null/undefined = unassigned). */
  value?: number | null
  onChange: (userId: number | null) => void
  disabled?: boolean
}

/**
 * Searchable single-user picker for task assignment (KQA-11).
 *
 * Domain-neutral: shows people by name/email, never role/dev jargon. Works with
 * zero integrations connected. Styling reuses the established slate palette
 * (#102a43/#486581/#bcccdc) already used across task forms — no new color values.
 */
export function UserPicker({ value, onChange, disabled }: UserPickerProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [users, setUsers] = useState<User[]>([])
  const [selected, setSelected] = useState<User | null>(null)
  const [loading, setLoading] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  // Resolve the selected user's display name (load list once to find it).
  useEffect(() => {
    let cancelled = false
    if (value == null) {
      setSelected(null)
      return
    }
    if (selected?.id === value) return
    usersService
      .get(value)
      .then((u) => {
        if (!cancelled) setSelected(u)
      })
      .catch((err) => {
        // Resolving an orphaned user id (deleted user, cross-org access)
        // — leave selected null so the picker shows the placeholder.
        if (!cancelled) console.warn('UserPicker.get failed:', err)
      })
    return () => {
      cancelled = true
    }
  }, [value])

  // Debounced search whenever the dropdown is open.
  useEffect(() => {
    if (!open) return
    setLoading(true)
    const handle = setTimeout(() => {
      usersService
        .assignable({ q: query || undefined, limit: 50 })
        .then((data) => setUsers(data))
        .catch(() => setUsers([]))
        .finally(() => setLoading(false))
    }, 200)
    return () => clearTimeout(handle)
  }, [open, query])

  // Close on outside click.
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [])

  const label = (u: User) => u.full_name || u.email

  const pick = (u: User | null) => {
    setSelected(u)
    onChange(u ? u.id : null)
    setOpen(false)
    setQuery('')
  }

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className="w-full px-4 py-2 border-2 border-[#bcccdc] rounded-lg focus:border-[#486581] focus:outline-none text-left text-[#2A3B4C] flex items-center justify-between disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <span className="flex items-center gap-2">
          <UserIcon className="w-4 h-4 text-[#486581]" />
          {selected ? label(selected) : (
            <span className="text-[#829ab1]">{t('task.assignee.placeholder')}</span>
          )}
        </span>
        {selected && !disabled && (
          <span
            role="button"
            aria-label={t('task.assignee.clear')}
            onClick={(e) => {
              e.stopPropagation()
              pick(null)
            }}
            className="text-[#829ab1] hover:text-[#102a43]"
          >
            <X className="w-4 h-4" />
          </span>
        )}
      </button>

      {open && (
        <div className="absolute z-20 mt-1 w-full bg-white border-2 border-[#bcccdc] rounded-lg shadow-lg max-h-72 overflow-hidden flex flex-col">
          <div className="p-2 border-b border-[#bcccdc] flex items-center gap-2">
            <Search className="w-4 h-4 text-[#829ab1]" />
            <input
              autoFocus
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('task.assignee.search')}
              className="w-full text-sm outline-none text-[#2A3B4C]"
            />
          </div>
          <ul className="overflow-y-auto">
            <li>
              <button
                type="button"
                onClick={() => pick(null)}
                className="w-full text-left px-4 py-2 text-sm text-[#556B7C] hover:bg-[#f0f4f8] flex items-center justify-between"
              >
                {t('task.assignee.unassigned')}
                {value == null && <Check className="w-4 h-4 text-[#486581]" />}
              </button>
            </li>
            {loading && (
              <li className="px-4 py-2 text-sm text-[#829ab1]">{t('common.loading')}</li>
            )}
            {!loading && users.length === 0 && (
              <li className="px-4 py-2 text-sm text-[#829ab1]">{t('task.assignee.noResults')}</li>
            )}
            {!loading &&
              users.map((u) => (
                <li key={u.id}>
                  <button
                    type="button"
                    onClick={() => pick(u)}
                    className="w-full text-left px-4 py-2 text-sm text-[#2A3B4C] hover:bg-[#f0f4f8] flex items-center justify-between"
                  >
                    <span className="flex flex-col">
                      <span>{label(u)}</span>
                      {u.full_name && <span className="text-xs text-[#829ab1]">{u.email}</span>}
                    </span>
                    {value === u.id && <Check className="w-4 h-4 text-[#486581]" />}
                  </button>
                </li>
              ))}
          </ul>
        </div>
      )}
    </div>
  )
}
